import { useState } from "react";
import { isDev, sleep } from "lib/useful";
import { store } from "react-recollect";
import Loading from "components/Loading";
import { API_URL, certPOST, certPOSTj } from "lib/API";
import HeaderBar from "components/HeaderBar";
import EmailDialogButton from "components/EmailDialogButton";
import { Button, ButtonSecondary } from "components/Buttons";
import PremierPhotoDialogButton from "components/PremierPhotoDialog";
import {
  ArrowUpIcon,
  BookOpenIcon,
  CheckIcon,
  InboxInIcon,
  TrendingDownIcon,
} from "@heroicons/react/outline";
import ButtonWithConfirmation from "components/ButtonWithConfirmation";
import { LoadingButtonSecondary } from "components/LoadingButtons";
import DigiPremPhotoCroppingDialogButton from "components/DigiPremPhotoCroppingDialog";
import { format } from "date-fns";
import Input from "components/Input";
import FileUploadButton from "components/FileUploadButton";

async function complete(product, _id) {
  if (!_id) throw Error("!_id");
  await certPOST("fulfilmentComplete", { _id });
  store[`fulfilments-${product}`][_id].complete = new Date();
}

async function completeAll(product) {
  return new Promise((resolve, reject) => {
    if (!store) reject("no store");
    if (!product) reject("no product");
    if (!store[`fulfilments-${product}`]) reject(`no fulfilments-${product}`);

    const fulfilments = Object.values(store[`fulfilments-${product}`] ?? {});

    const notComplete = fulfilments?.filter((f) => !f.complete);
    if (notComplete.length > 0) {
      setTimeout(
        async () => {
          // await certPOST("fulfilmentComplete", { _id: notComplete[0]._id });
          // notComplete[0].complete = new Date();
          await complete(product, notComplete[0]._id);
          completeAll(product);
        },
        100 + Math.random() * 400,
      );
    } else resolve();
  });
}

function getPhotoDownloadFilename(fulfilment) {
  let f = "premierPhoto";
  if (fulfilment.researchId) {
    const r =
      store.researchDataByCertId &&
      store.researchDataByCertId[fulfilment.researchId];
    if (r) {
      const c = r.certNumber;
      if (c) f = c.replace("/", "_");
    }
  }
  return f;
}

async function handleReplacePhotoUploadComplete(r, refresh) {
  const { success, path, id } = r;
  if (success) {
    const swapPhotoResult = await certPOSTj(`swapPhoto`, { path, id });
    if (swapPhotoResult.success) {
      refresh();
    }
  } else console.error(r);
}

async function handleResetPhoto(id, refresh) {
  await certPOSTj(`resetPhoto`, { id });
  refresh();
}

async function changeProductType(id, toType) {
  await certPOSTj("changeFulfilmentProductType", { id, toType });
  window.location.reload();
}

function getPhotoDownloadLink(fulfilment) {
  const f = getPhotoDownloadFilename(fulfilment);

  return (
    (isDev() ? "//localhost:4040" : "") +
    `/api/premierProxy/${f}.jpg?url=` +
    encodeURIComponent(
      (fulfilment.photoUrl || "").replace("https://cdn.shopify.com", ""),
    ) +
    ("&token=" + store.token)
  );
}

async function sendEmail(fulfilment, overrideEmailTo) {
  const r = await certPOSTj(`emailFulfilment-${fulfilment.product}`, {
    ...fulfilment,
    email: overrideEmailTo,
  });
  return r.success;
}

async function handleSendAll(setSendingAll, product) {
  setSendingAll(<Loading text="Sending all emails" />);
  const r = await certPOSTj(`emailFulfilment-${product}`, {
    all: true,
  });
  if (r.success) {
    completeAll(product);
    setSendingAll(null);
  } else setSendingAll(<p>An error occured. {r.error}</p>);
}

function Emailer({ product, fulfilment, onSent }) {
  const shOrder = store.shopifyOrders?.[fulfilment.shopifyOrdersId];
  const [email, setEmail] = useState(
    isDev()
      ? `tris@bmm.co.uk?subject=${encodeURIComponent(shOrder.email)}`
      : shOrder.email,
  );

  const [state, setState] = useState(null);
  async function send() {
    setState("emailing");
    const er = await sendEmail(fulfilment, email);
    if (!er) {
      console.error({ er });
      setState("Error emailing");
      return;
    }
    setState("Complete, finishing");
    if (store[`fulfilments-${product}`]?.[fulfilment._id])
      store[`fulfilments-${product}`][fulfilment._id].complete = new Date();
    await sleep(Math.random() * 5000);
    onSent?.(fulfilment);
    setState("Done");
  }
  return (
    <div className="flex">
      <Input
        value={state ?? email}
        setValue={setEmail}
        className="w-96"
        disabled={state}
      />
      <ButtonSecondary onClick={send} disabled={state}>
        send
      </ButtonSecondary>
    </div>
  );
}

const HeaderBarEmailAll = ({ product, setSendingAll }) => (
  <HeaderBar>
    <div className="btn-group">
      <Button
        message="Really send ALL EMAILS?"
        onClick={() => handleSendAll(setSendingAll, product)}
        color="blue"
        className="text-white"
      >
        <InboxInIcon className="size-6 mr-2" />
        Email all
      </Button>
      <ButtonWithConfirmation
        className="btn-secondary"
        onConfirm={() => completeAll(product)}
        message="Really complete ALL items?"
        color="blue"
      >
        <CheckIcon className="size-6 mr-2" />
        Complete all
      </ButtonWithConfirmation>
    </div>
  </HeaderBar>
);

const HeaderBarDownloadAll = ({ product }) => {
  const YMD = format(new Date(), "yyyy-MM-dd");
  return (
    <HeaderBar>
      <div className="btn-group">
        <a
          target="_blank"
          rel="noreferrer"
          download={`certificates_${YMD}.pdf`}
          href={`${API_URL}pdfFulfilments-${product}/${product}_${YMD}.pdf?token=${store.token}`}
        >
          <ButtonSecondary>Download all</ButtonSecondary>
        </a>
        <ButtonWithConfirmation
          className="btn-secondary"
          onConfirm={() => completeAll(product)}
          message="Really complete ALL items?"
          color="blue"
        >
          <CheckIcon className="size-6 mr-2" />
          Complete all
        </ButtonWithConfirmation>
      </div>
    </HeaderBar>
  );
};

const HeaderBarCompleteAll = ({ product }) => (
  <HeaderBar>
    <div className="btn-group">
      <ButtonWithConfirmation
        className="btn-secondary"
        onConfirm={() => completeAll(product)}
        message="Really complete ALL items?"
        color="blue"
      >
        <CheckIcon className="size-6 mr-2" />
        Complete all
      </ButtonWithConfirmation>
    </div>
  </HeaderBar>
);

const FulfilmentHeaderBar = ({ product, setSendingAll }) =>
  ({
    email: (
      <HeaderBarEmailAll product={product} setSendingAll={setSendingAll} />
    ),
    digicert: (
      <HeaderBarEmailAll product={product} setSendingAll={setSendingAll} />
    ),
    digipremcert: (
      <HeaderBarEmailAll product={product} setSendingAll={setSendingAll} />
    ),
    premPhoto: <HeaderBarCompleteAll product={product} />,
    cert: <HeaderBarDownloadAll product={product} />,
    head: <HeaderBarDownloadAll product={product} />,
  })[product];

// const FulfilmentOrderLine = (props) => (
// <pre>{JSON.stringify(props, null, 2)}</pre>
// );
const FulfilmentOrderLine = ({
  product,
  setPrintDialog,
  setPrintDialogURL,
  refresh,
  imageReloadCounter,
  setImageReloadCounter,
  fulfilment,
}) =>
  ({
    email: (
      <div className="btn-group">
        <EmailDialogButton fulfilmentId={fulfilment._id} />
      </div>
    ),
    digicert: (
      <>
        <div className="btn-group">
          <ButtonSecondary
            onClick={() =>
              setPrintDialogURL("digicert", fulfilment, [
                {
                  jsx: (
                    <Emailer
                      key="emailer"
                      product={product}
                      fulfilment={fulfilment}
                      onSent={() => setPrintDialog(null)}
                    />
                  ),
                },
              ])
            }
          >
            <BookOpenIcon className="size-6 mr-2" />
            Open
          </ButtonSecondary>

          <ButtonSecondary
            id={fulfilment._id}
            onClick={() => changeProductType(fulfilment._id, "digipremcert")}
          >
            Upgrade to DigiPremCert
          </ButtonSecondary>
        </div>
        <div className="mt-2 max-w-md flex gap-2">
          <Emailer product={product} fulfilment={fulfilment} />
          <LoadingButtonSecondary
            onClick={async () => await complete(product, fulfilment._id)}
          >
            Complete (without emailing!)
          </LoadingButtonSecondary>
        </div>
      </>
    ),
    digipremcert: (
      <>
        <div className="btn-group">
          <FileUploadButton
            onUploadComplete={(r) =>
              handleReplacePhotoUploadComplete(
                { ...r, id: fulfilment._id },
                refresh,
              )
            }
          >
            <ArrowUpIcon className="mr-2 size-6" />
            Replace photo
          </FileUploadButton>
          {fulfilment.overridePhoto && (
            <Button onClick={() => handleResetPhoto(fulfilment._id, refresh)}>
              Reset photo
            </Button>
          )}
          <DigiPremPhotoCroppingDialogButton
            fulfilment={fulfilment}
            onClose={() => setImageReloadCounter((c) => c + 1)}
          />
          <ButtonSecondary
            onClick={() =>
              setPrintDialogURL("digipremcert", fulfilment, [
                {
                  jsx: (
                    <Emailer
                      key="emailer"
                      product={product}
                      fulfilment={fulfilment}
                      onSent={() => setPrintDialog(null)}
                    />
                  ),
                },
              ])
            }
          >
            <BookOpenIcon className="size-6 mr-2" />
            Open
          </ButtonSecondary>
          <button
            className="bg-red-900 text-yellow-300 btn"
            id={fulfilment._id}
            onClick={() => changeProductType(fulfilment._id, "digicert")}
          >
            <TrendingDownIcon className="size-6 mr-2" />
            Downgrade to DigiCert
          </button>
        </div>
        <div className="mt-2 max-w-md flex gap-2">
          <Emailer product={product} fulfilment={fulfilment} />
          <LoadingButtonSecondary
            onClick={async () => await complete(product, fulfilment._id)}
          >
            Complete (without emailing!)
          </LoadingButtonSecondary>
        </div>
        <div className="flex">
          <img
            key={imageReloadCounter}
            src={`${API_URL}cropPremThumbnail/${fulfilment._id}.png?iRC=${imageReloadCounter}&token=${store.token}`}
            className="m-4 inline-block border-2 border-black object-contain"
            style={{ width: 180 * 2, height: 70 * 2 }}
            alt="Premier"
          />
        </div>
      </>
    ),
    premphoto: (
      <>
        <img
          src={fulfilment.photoUrl}
          width={148}
          height={105}
          alt=""
          className="border-2 border-black mx-2 mb-2"
        />
        <div className="btn-group">
          <PremierPhotoDialogButton fulfilment={fulfilment} />
          <ButtonSecondary
            onClick={() => (window.location = getPhotoDownloadLink(fulfilment))}
          >
            Download image {getPhotoDownloadFilename(fulfilment)}
          </ButtonSecondary>
          <LoadingButtonSecondary
            onClick={async () => await complete(product, fulfilment._id)}
          >
            Complete
          </LoadingButtonSecondary>
        </div>
      </>
    ),
    cert: (
      <div className="btn-group">
        <ButtonSecondary onClick={() => setPrintDialogURL("cert", fulfilment)}>
          <BookOpenIcon className="size-6 mr-2" />
          Open
        </ButtonSecondary>
        <LoadingButtonSecondary
          onClick={async () => await complete(product, fulfilment._id)}
        >
          Complete
        </LoadingButtonSecondary>
      </div>
    ),
    head: null,
  })[product.toLowerCase()];

export { FulfilmentHeaderBar, FulfilmentOrderLine };
